import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { diag, DiagConsoleLogger, DiagLogLevel, trace, context } from '@opentelemetry/api';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { W3CTraceContextPropagator } from '@opentelemetry/core';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction';
import { MeterProvider, ConsoleMetricExporter, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics';
import { metrics } from '@opentelemetry/api';
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http';
import { LoggerProvider, SimpleLogRecordProcessor } from '@opentelemetry/sdk-logs';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import { logs } from '@opentelemetry/api-logs';
import { Resource } from '@opentelemetry/resources';

class OpenTelemetrySetup {
  constructor(config) {
    this.tracer = trace.getTracer('default');
    this.consoleLogger = null;
    this.configVariables = config || {};
    this.metricReader = null;
    this.httpRequestCounter = null;
    this.responseTimeHistogram = null;
    this.latestTraceId = '';
    this.rootSpan = null;
    this.tracerProvider = null;
    this.loggerProvider = null;
    this.meterProvider = null;
    this.traceExporter = null;
    this.logExporter = null;
    this.metricExporter = null;
    this.otelInitialized = false;
    this.networkAvailable = true; // Default to true, can be updated as needed

    window.addEventListener('online', () => this.handleNetworkChange(true));
    window.addEventListener('offline', () => this.handleNetworkChange(false));
  }

  handleNetworkChange(isOnline) {
    this.networkAvailable = isOnline;
    if (isOnline) {
      if (!this.otelInitialized) {
        this.init();
      }
    } else {
      this.networkAvailable = false;
      this.shutdown();
    }
  }

  init() {
    if (!this.networkAvailable) {
      console.warn('[OpenTelemetrySetup] Network unavailable, skipping OpenTelemetry setup.');
      this.otelInitialized = false;
      return;
    }

    if (!this.configVariables.otelTraceUrl || !this.configVariables.otelLogsUrl || !this.configVariables.otelMetricsUrl) {
      console.warn('[OpenTelemetrySetup] Configuration not available, skipping OpenTelemetry setup.');
      this.otelInitialized = false;
      return;
    }

    this.otelInitialized = true;

    try {
      const resource = this.createResource();

      diag.setLogger(new DiagConsoleLogger(), DiagLogLevel.DEBUG);

      // TRACE EXPORTER
      this.traceExporter = new OTLPTraceExporter({
        url: this.configVariables.otelTraceUrl,
        headers: { 'Content-Type': 'application/json' },
      });

      this.tracerProvider = new WebTracerProvider({ resource });

      this.tracerProvider.register({
        contextManager: new ZoneContextManager(),
        propagator: new W3CTraceContextPropagator(),
      });

      registerInstrumentations({
        instrumentations: [
          new DocumentLoadInstrumentation(),
          new FetchInstrumentation(),
          new XMLHttpRequestInstrumentation(),
          new UserInteractionInstrumentation(),
        ],
      });

      this.tracer = trace.getTracer('default');

      // Create root span for single-trace session
      this.rootSpan = this.tracer.startSpan('otel-service-root');
      this.latestTraceId = this.rootSpan.spanContext().traceId;

      // Force all spans to adopt the root traceId
      const rootTraceId = this.latestTraceId;
      const forceTraceIdProcessor = {
        onStart: (span) => {
          if (span !== this.rootSpan && span._spanContext) {
            span._spanContext = { ...span._spanContext, traceId: rootTraceId };
          }
        },
        onEnd() {},
        shutdown: async () => {},
        forceFlush: async () => {},
      };
      this.tracerProvider.addSpanProcessor(forceTraceIdProcessor);

      this.tracerProvider.addSpanProcessor(
        new BatchSpanProcessor(this.traceExporter)
      );

      // METRICS SETUP
      this.ensureInitialized();

      // LOGGING EXPORTER
      this.logExporter = new OTLPLogExporter({
        url: this.configVariables.otelLogsUrl,
        headers: { 'Content-Type': 'application/json' },
      });

      this.loggerProvider = new LoggerProvider({ resource });
      this.loggerProvider.addLogRecordProcessor(
        new SimpleLogRecordProcessor(this.logExporter)
      );

      logs.setGlobalLoggerProvider(this.loggerProvider);
      this.consoleLogger = this.loggerProvider.getLogger('console-logger');

      this.createConsoleError();

    } catch (error) {
      console.error('[OpenTelemetrySetup] Error during OpenTelemetry initialization:', error);
      this.otelInitialized = false;
    }
  }

  async shutdown() {
    this.otelInitialized = false;
    try {
      if (this.tracerProvider) {
        await this.tracerProvider.shutdown();
        this.tracerProvider = null;
      }
      if (this.loggerProvider) {
        await this.loggerProvider.shutdown();
        this.loggerProvider = null;
      }
      if (this.meterProvider) {
        await this.meterProvider.shutdown();
        this.meterProvider = null;
      }
      if (this.traceExporter && this.traceExporter.shutdown) {
        await this.traceExporter.shutdown();
        this.traceExporter = null;
      }
      if (this.logExporter && this.logExporter.shutdown) {
        await this.logExporter.shutdown();
        this.logExporter = null;
      }
      if (this.metricExporter && this.metricExporter.shutdown) {
        await this.metricExporter.shutdown();
        this.metricExporter = null;
      }
    } catch (e) {
      console.warn('[OpenTelemetrySetup] Error during OpenTelemetry shutdown:', e);
    }
    this.tracer = null;
    this.consoleLogger = null;
    this.httpRequestCounter = null;
    this.responseTimeHistogram = null;
    this.metricReader = null;
    console.warn('[OpenTelemetrySetup] Network disconnected, OpenTelemetry actions are paused.');
  }

  createResource() {
    const {
      otelServiceName,
      otelServiceVersion,
      otelDeploymentEnvironment,
      otelServiceNamespace,
      otelServiceType,
      otelApmServiceType,
    } = this.configVariables;
    return new Resource({
      'service.name': otelServiceName,
      'service.version': otelServiceVersion,
      'deployment.environment': otelDeploymentEnvironment,
      'service.namespace': otelServiceNamespace,
      'service.type': otelServiceType,
      'service.service_type': otelApmServiceType
    });
  }

  ensureInitialized() {
    const resource = this.createResource();
    console.log('[OTEL DEBUG] Setting up metrics...');

    const otlpMetricExporter = new OTLPMetricExporter({
      url: this.configVariables.otelMetricsUrl,
      headers: {},
    });

    const consoleMetricExporter = new ConsoleMetricExporter();

    const otlpMetricReader = new PeriodicExportingMetricReader({
      exporter: otlpMetricExporter,
      exportIntervalMillis: 30000,
      exportTimeoutMillis: 15000,
    });

    const consoleMetricReader = new PeriodicExportingMetricReader({
      exporter: consoleMetricExporter,
      exportIntervalMillis: 30000,
      exportTimeoutMillis: 15000,
    });

    const meterProvider = new MeterProvider({
      resource: resource,
      readers: [otlpMetricReader, consoleMetricReader],
    });

    this.meterProvider = meterProvider;
    this.metricExporter = otlpMetricExporter;

    metrics.setGlobalMeterProvider(meterProvider);
    console.log('[OTEL DEBUG] Metrics configured with automatic periodic export (10s intervals)');

    window.OTEL_EXPORT_METRICS = async () => {
      try {
        console.log('[OTEL DEBUG] Manually forcing metrics export due to route change...');
        await meterProvider.forceFlush();
        console.log('[OTEL DEBUG] Manual metrics export completed');
      } catch (error) {
        console.error('[OTEL ERROR] Failed to manually export metrics:', error);
      }
    };

    console.log('[OTEL DEBUG] Route change detection configured for additional metric exports');

    const meter = meterProvider.getMeter('web-meter');
    this.httpRequestCounter = meter.createCounter('web_http_requests');
    this.interactionCounter = meter.createCounter('web_user_interactions');
    this.httpRequestCounter.add(1, {
      path: window.location.pathname,
      environment: window.OTEL_SERVICE_ENVIRONMENT
    });

    this.responseTimeHistogram = meter.createHistogram('web_http_response_time');
    const simulatedResponseTime = Date.now() % 1000;
    this.responseTimeHistogram.record(simulatedResponseTime, {
      path: window.location.pathname,
      method: 'GET',
    });

    const memoryHistogram = meter.createHistogram('web_memory_usage_bytes', {
      description: 'Memory usage in bytes',
      unit: 'bytes',
    });

    if (performance.memory) {
      memoryHistogram.record(performance.memory.usedJSHeapSize, {
        'memory_type': 'used_heap',
        'page': window.location.pathname
      });
    }

    document.addEventListener('click', (event) => {
      if (this.interactionCounter) {
        this.interactionCounter.add(1, {
          'interaction_type': 'click',
          'element': event.target.tagName.toLowerCase(),
          'page': window.location.pathname
        });
      }
    });
  }

  isTelemetryActive() {
    return !!(this.networkAvailable && this.otelInitialized && this.tracer && this.consoleLogger);
  }

  getCurrentTraceId() {
    if (!this.isTelemetryActive() || !this.networkAvailable) return null;
    return this.latestTraceId || (this.rootSpan ? this.rootSpan.spanContext().traceId : null);
  }

  async logConsoleMessage(level, message, ...optionalParams) {
    // Add recursion guard at the start of logConsoleMessage
    if (this._isLoggingMessage) {
      return;
    }  
    if (!this.isTelemetryActive()) {
      return;
    }

    this._isLoggingMessage = true;

    const serviceType = this.configVariables.otelApmServiceType;
    const span = this.tracer.startSpan(`console-${level}`);
    const ctx = trace.setSpan(context.active(), span);

    try {
      await context.with(ctx, async () => {
        const otelUrls = [
          this.configVariables && this.configVariables.otelTraceUrl,
          this.configVariables && this.configVariables.otelLogsUrl,
          this.configVariables && this.configVariables.otelMetricsUrl,
        ].filter(Boolean);

        const messageStr = typeof message === 'string' ? message : JSON.stringify(message);
        const paramsStr = JSON.stringify(optionalParams);

        if (otelUrls.some((url) => (url && messageStr.includes(url)) || (url && paramsStr.includes(url)))) {
          return;
        }

        const traceId = this.getCurrentTraceId();
        let errorCode = '';
        if (optionalParams && optionalParams.length > 0) {
          const firstParam = optionalParams[0];
          if (firstParam && typeof firstParam === 'object') {
            if (firstParam.status && firstParam.status !== 0) {
              errorCode = `[Code: ${firstParam.status}] `;
            } else if (firstParam.code) {
              errorCode = `[Code: ${firstParam.code}] `;
            } else if (firstParam.error && firstParam.error.name) {
              errorCode = `[${firstParam.error.name}] `;
            }
          }
        }

        const logBody = traceId
          ? `[${level.toUpperCase()}] [service_type: ${serviceType}] [TraceID: ${traceId}] ${errorCode}${message}`
          : `[${level.toUpperCase()}] [service_type: ${serviceType}] ${errorCode}${message}`;

        const logPayload = {
          body: logBody,
          attributes: {
            level,
            serviceType,
            traceId: traceId || '',
            trace_id: traceId || '',
            optionalParams: JSON.stringify(optionalParams),
            timestamp: new Date().toISOString(),
          },
        };

        await this.retryEmitLog(logPayload, 3);
      });
    } catch (err) {
      // Silently fail to prevent recursion
      console.warn('[OpenTelemetry] Failed to log message:', err.message);
    } finally {
      span.end();
      this._isLoggingMessage = false;
    }
  }

// Retry mechanism for log export (maxRetry = 3)
  async retryEmitLog(logPayload, maxRetry = 3) {
    let attempt = 0;
    const emitLog = () => {
      return new Promise((resolve, reject) => {
        try {
          if (this.consoleLogger && typeof this.consoleLogger.emit === 'function') {
            this.consoleLogger.emit(logPayload);
            resolve();
          } else {
            reject(new Error('consoleLogger.emit is not a function'));
          }
        } catch (err) {
          reject(err);
        }
      });
    };

    while (attempt < maxRetry) {
      try {
        await emitLog();
        return;
      } catch (err) {
        attempt++;
        if (attempt >= maxRetry) {
          return;
        }
        await new Promise(res => setTimeout(res, 1000));
      }
    }
  }

  createConsoleError() {
    const self = this;
    const originalConsoleError = console.error;
    // Add recursion guard to prevent infinite loop
    let isLoggingError = false;

    console.error = function(message, ...optionalParams) {
      originalConsoleError.apply(console, [message, ...optionalParams]);
      // Prevent recursive calls
      if (isLoggingError) {
        return;
      }

      try {
        isLoggingError = true;
      self.logConsoleMessage('error', message, ...optionalParams);
      } catch (err) {
        // If error occurs during logging, use original console.error to avoid recursion
        originalConsoleError.call(console, '[OpenTelemetry] Error in logConsoleMessage:', err);
      } finally {
        isLoggingError = false;
      }
    };

    window.onerror = function(message, source, lineno, colno, error) {
      if (!self.isTelemetryActive()) return false;
      const errorMessage = typeof message === 'string' ? message : 'Error event captured';
      self.logConsoleMessage('error', errorMessage, {
        source,
        lineno,
        colno,
        error: error ? error.toString() : null,
        stack: error && error.stack,
      });
      return false;
    };

    window.addEventListener('error', function(event) {
      if (!self.isTelemetryActive()) return;
      if (event.target && event.target !== window) {
        var target = event.target;
        var errorMessage = '';
        var errorDetails = {};
        if (target.tagName === 'LINK') {
          errorMessage = `Failed to load stylesheet: ${target.href}`;
          errorDetails = { type: 'stylesheet_load_error', href: target.href, tagName: target.tagName, error: 'Resource loading failed' };
        } else {
          errorMessage = `Failed to load resource: ${target.src || target.href || 'unknown'}`;
          errorDetails = { type: 'resource_load_error', src: target.src || target.href, tagName: target.tagName, error: 'Resource loading failed' };
        }
        self.logConsoleMessage('error', errorMessage, errorDetails);
      }
    }, true);

    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver(function(list) {
          if (!self.isTelemetryActive()) return;
          for (var i = 0; i < list.getEntries().length; i++) {
            var entry = list.getEntries()[i];
            if (entry.entryType === 'resource') {
              var resourceEntry = entry;
              if (resourceEntry.duration > 5000 ||
                  (resourceEntry.responseStart === 0 && resourceEntry.responseEnd === 0)) {
                var errorMessage = `Resource failed to load: ${resourceEntry.name}`;
                var errorDetails = {
                  type: 'performance_resource_error',
                  url: resourceEntry.name,
                  duration: resourceEntry.duration,
                  responseStart: resourceEntry.responseStart,
                  responseEnd: resourceEntry.responseEnd,
                  transferSize: resourceEntry.transferSize,
                  initiatorType: resourceEntry.initiatorType
                };
                self.logConsoleMessage('error', errorMessage, errorDetails);
              }
            }
          }
        });
        observer.observe({ entryTypes: ['resource'] });
      } catch (e) {
        console.warn('[OpenTelemetrySetup] PerformanceObserver not supported or failed to initialize:', e);
      }
    }

    const originalXHROpen = XMLHttpRequest.prototype.open;
    const originalXHRSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function(method, url, async = true, username, password) {
      this._otelMethod = method;
      this._otelUrl = url;
      return originalXHROpen.apply(this, [method, url, async, username, password]);
    };
    XMLHttpRequest.prototype.send = function(body) {
      const xhr = this;
      this.addEventListener('error', function() {
        if (!self.isTelemetryActive() || !self.networkAvailable) return;
        const errorMessage = `Failed to load resource: ${xhr._otelMethod} ${xhr._otelUrl}`;
        self.logConsoleMessage('error', errorMessage, {
          status: xhr.status,
          statusText: xhr.statusText,
          method: xhr._otelMethod,
          url: xhr._otelUrl,
          readyState: xhr.readyState,
        });
      });
      this.addEventListener('load', function() {
        if (!self.isTelemetryActive() || !self.networkAvailable) return;
        if (xhr.status >= 400) {
          const errorMessage = `${xhr._otelMethod} ${xhr._otelUrl} ${xhr.status} (${xhr.statusText})`;
          let responseText;
          try {
            responseText = xhr.responseText && typeof xhr.responseText === 'string' 
              ? xhr.responseText.substring(0, 500) 
              : xhr.responseText;
          } catch (e) {
            responseText = undefined;
          }
          
          self.logConsoleMessage('error', errorMessage, {
            status: xhr.status,
            statusText: xhr.statusText,
            method: xhr._otelMethod,
            url: xhr._otelUrl,
            response: responseText,
          });
        }
      });
      this.addEventListener('timeout', function() {
        if (!self.isTelemetryActive() || !self.networkAvailable) return;
        const errorMessage = `${xhr._otelMethod} ${xhr._otelUrl} timed out`;
        self.logConsoleMessage('warning', errorMessage, {
          method: xhr._otelMethod,
          url: xhr._otelUrl,
          timeout: xhr.timeout,
        });
      });
      return originalXHRSend.apply(this, [body]);
    };
  }
}

// Create and export a singleton instance
export const openTelemetrySetup = new OpenTelemetrySetup();

if (typeof window !== 'undefined') {
  window.openTelemetrySetup = openTelemetrySetup;
}

function getOtelConfigFromGlobals() {
  // Use global variables injected by the backend (footer-js.twig)
  return {
    otelTraceUrl: window.__OTEL_TRACE_URL || '',
    otelLogsUrl: window.__OTEL_LOG_URL || '',
    otelMetricsUrl: window.__OTEL_METRIC_URL || '',
    otelServiceName: window.__OTEL_SERVICE_NAME || '',
    otelServiceVersion: window.__OTEL_SERVICE_VERSION || '',
    otelDeploymentEnvironment: window.__OTEL_DEPLOYMENT_ENV || '',
    otelServiceNamespace: window.__OTEL_SERVICE_NAMESPACE || '',
    otelServiceType: window.__OTEL_SERVICE_TYPE || '',
    otelApmServiceType: window.__OTEL_APM_SERVICE_TYPE || '',
    debug: typeof window.__DEBUG !== 'undefined' ? window.__DEBUG : false,
  };
}

// Initialize OpenTelemetry using globals from footer-js.twig
const otelConfig = getOtelConfigFromGlobals();
const otelSetup = new OpenTelemetrySetup(otelConfig);
otelSetup.init();
setTimeout(async () => {
  try {
    console.log('[OTEL DEBUG] Shutting down metrics export after 10s');
    if (otelSetup.meterProvider) {
      await otelSetup.meterProvider.shutdown();
    }
  } catch (err) {
    console.error('[OTEL ERROR] Error shutting down metrics:', err);
  }
}, 15000);
